import { motion } from "framer-motion";
import { Heart, Users, Calendar, Dog } from "lucide-react";
import { Link } from "react-router-dom";

const COMMUNITY_IMG = "https://images.unsplash.com/photo-1761055923367-ea32773bb5d5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGRvZyUyMG93bmVyJTIwaHVnfGVufDB8fHx8MTc3NTk3MTUxM3ww&ixlib=rb-4.1.0&q=85";

const ways = [
  {
    icon: Heart,
    title: "Donate",
    desc: "Every dollar helps provide food, supplies, and veterinary care for pets in need.",
    link: "/donate",
    linkText: "Make a Donation",
  },
  {
    icon: Users,
    title: "Volunteer",
    desc: "Join our team to help with grooming, fundraisers, pantry distribution, and events.",
    link: "/volunteer",
    linkText: "Sign Up to Volunteer",
  },
  {
    icon: Calendar,
    title: "Fundraisers & Events",
    desc: "Help organize doggy activities, entertainment, and fundraising events in your community.",
  },
  {
    icon: Dog,
    title: "Spread the Word",
    desc: "Share our mission on social media. Help us go global and change lives.",
  },
];

export default function GetInvolved() {
  return (
    <section id="get-involved" data-testid="get-involved-section" className="py-24 md:py-32 bg-[#FDFBF7] relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="order-2 lg:order-1"
          >
            <div className="rounded-3xl overflow-hidden shadow-[0_8px_32px_rgba(44,44,42,0.08)]">
              <img
                src={COMMUNITY_IMG}
                alt="Happy dog owner"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="order-1 lg:order-2"
          >
            <p className="font-body text-xs font-bold tracking-[0.2em] uppercase text-[#C0603A] mb-4">
              Make a Difference
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight text-[#2C2C2A] font-medium mb-10">
              How You Can Help
            </h2>

            <div className="space-y-6">
              {ways.map((w, i) => (
                <motion.div
                  key={w.title}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className="flex gap-4"
                  data-testid={`way-to-help-${w.title.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <div className="w-12 h-12 rounded-2xl bg-[#C0603A]/10 flex items-center justify-center flex-shrink-0">
                    <w.icon className="w-5 h-5 text-[#C0603A]" />
                  </div>
                  <div>
                    <h4 className="font-heading text-xl text-[#2C2C2A] mb-1">{w.title}</h4>
                    <p className="font-body text-sm text-[#6C6B66] leading-relaxed mb-1">{w.desc}</p>
                    {w.link && (
                      <Link
                        to={w.link}
                        className="font-body text-sm font-semibold text-[#C0603A] hover:text-[#A64D29] transition-colors"
                        data-testid={`way-link-${w.title.toLowerCase().replace(/\s/g, "-")}`}
                      >
                        {w.linkText} &rarr;
                      </Link>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
